import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';
import { invalidateScheduleExpiryCache } from '../ScheduleExpiryBanner';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import DoctorSchedulesJsonPanel from './DoctorSchedulesJsonPanel';

dayjs.extend(utc);

const DoctorSchedulesSection = ({ clinicId }) => {
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formVisible, setFormVisible] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [overlappingSlots, setOverlappingSlots] = useState({});

  // Default schedule template
  const defaultSchedule = {
    "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [], "PH": [],
  };

  // New or edited schedule
  const [scheduleForm, setScheduleForm] = useState({
    id: null,
    from_date: dayjs().format('YYYY-MM-DD'),
    to_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    is_default_schedule: false,
    active: true,
    schedule: { ...defaultSchedule }
  });

  const weekDays = [
    { value: "0", label: 'Monday' },
    { value: "1", label: 'Tuesday' },
    { value: "2", label: 'Wednesday' },
    { value: "3", label: 'Thursday' },
    { value: "4", label: 'Friday' },
    { value: "5", label: 'Saturday' },
    { value: "6", label: 'Sunday' },
    { value: "PH", label: 'Public Holiday' }
  ];

  // Utility function to ensure schedules have proper format
  const formatSchedules = (schedules) => {
    return (schedules || []).map(schedule => {
      // Ensure schedule object has the expected structure
      const formattedSchedule = { ...schedule };
      if (!formattedSchedule.schedule) {
        formattedSchedule.schedule = { ...defaultSchedule };
      } else {
        // Ensure each day has an array, even if empty
        weekDays.forEach(day => {
          if (!Array.isArray(formattedSchedule.schedule[day.value])) {
            formattedSchedule.schedule[day.value] = [];
          }
        });
      }
      return formattedSchedule;
    });
  };

  // Load doctors when component mounts
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoading(true);
        const data = await clinicService.getClinicDoctors(clinicId);
        setDoctors(data.doctors || []);
        if (data.doctors && data.doctors.length > 0) {
          setSelectedDoctor(data.doctors[0]);
        }
      } catch (err) {
        console.error('Error loading doctors:', err);
        setError('Failed to load doctors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadDoctors();
  }, [clinicId]);

  // Load schedules when selected doctor changes
  useEffect(() => {
    const loadSchedules = async () => {
      if (!selectedDoctor) return;

      try {
        setLoading(true);
        const data = await clinicService.getDoctorSchedules(clinicId, selectedDoctor.id);
        setSchedules(formatSchedules(data.schedules));
      } catch (err) {
        console.error('Error loading schedules:', err);
        setError('Failed to load schedules. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSchedules();
  }, [clinicId, selectedDoctor]);

  const handleDoctorSelect = (event) => {
    const doctorId = parseInt(event.target.value);
    const doctor = doctors.find(doc => doc.id === doctorId);
    setSelectedDoctor(doctor);
  };

  const handleAddSchedule = () => {
    setScheduleForm({
      id: null,
      from_date: dayjs().format('YYYY-MM-DD'),
      to_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      is_default_schedule: false,
      active: true,
      schedule: { ...defaultSchedule }
    });
    setEditingScheduleId(null);
    setFormVisible(true);
  };

  const handleEditSchedule = (schedule) => {
    // Create a copy of the schedule with proper structure for editing
    const editableSchedule = formatSchedules([schedule])[0];

    setScheduleForm({
      id: editableSchedule.id,
      from_date: editableSchedule.from_date ? dayjs(editableSchedule.from_date).format('YYYY-MM-DD') : null,
      to_date: editableSchedule.to_date ? dayjs(editableSchedule.to_date).format('YYYY-MM-DD') : null,
      is_default_schedule: editableSchedule.is_default_schedule,
      active: editableSchedule.active,
      schedule: { ...editableSchedule.schedule }
    });
    setEditingScheduleId(schedule.id);
    setFormVisible(true);
  };

  const handleFormInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Update date fields when default schedule checkbox changes
  const handleDefaultScheduleChange = (e) => {
    const isDefault = e.target.checked;
    setScheduleForm(prev => ({
      ...prev,
      is_default_schedule: isDefault,
      from_date: isDefault ? null : dayjs().format('YYYY-MM-DD'),
      to_date: isDefault ? null : dayjs().add(1, 'year').format('YYYY-MM-DD')
    }));
  };

  const handleAddTimeSlot = (dayIndex) => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };
      // Make sure we create a new array instead of mutating the existing one
      const daySchedule = Array.isArray(updatedSchedule.schedule[dayIndex])
        ? [...updatedSchedule.schedule[dayIndex]]
        : [];

      // Add a single new time slot
      daySchedule.push({ from: "09:00", to: "17:00" });

      // Update the schedule for this day
      updatedSchedule.schedule = {
        ...updatedSchedule.schedule,
        [dayIndex]: daySchedule
      };

      return updatedSchedule;
    });
  };

  const handleRemoveTimeSlot = (dayIndex, slotIndex) => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };
      updatedSchedule.schedule[dayIndex] = [
        ...updatedSchedule.schedule[dayIndex].slice(0, slotIndex),
        ...updatedSchedule.schedule[dayIndex].slice(slotIndex + 1)
      ];
      return updatedSchedule;
    });
  };

  // Helper function to check if time slots overlap
  const checkForOverlaps = (dayIndex, slots) => {
    if (!slots || slots.length <= 1) return false;

    // Sort slots by start time
    const sortedSlots = [...slots].sort((a, b) => a.from.localeCompare(b.from));

    const overlaps = [];
    for (let i = 0; i < sortedSlots.length - 1; i++) {
      const currentSlot = sortedSlots[i];
      const nextSlot = sortedSlots[i + 1];

      if (currentSlot.to > nextSlot.from) {
        // Find original indices of these slots
        const currentIndex = slots.findIndex(
          slot => slot.from === currentSlot.from && slot.to === currentSlot.to
        );
        const nextIndex = slots.findIndex(
          slot => slot.from === nextSlot.from && slot.to === nextSlot.to
        );

        overlaps.push(currentIndex, nextIndex);
      }
    }

    return overlaps.length > 0 ? [...new Set(overlaps)] : false;
  };

  // Check for overlaps when scheduleForm changes
  useEffect(() => {
    if (!formVisible) return;

    const newOverlappingSlots = {};

    for (const dayIndex of Object.keys(scheduleForm.schedule)) {
      const daySlots = scheduleForm.schedule[dayIndex];
      const overlappingIndices = checkForOverlaps(dayIndex, daySlots);

      if (overlappingIndices) {
        newOverlappingSlots[dayIndex] = overlappingIndices;
      }
    }

    setOverlappingSlots(newOverlappingSlots);
  }, [scheduleForm.schedule, formVisible]);

  const handleTimeSlotChange = (dayIndex, slotIndex, field, value) => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };
      updatedSchedule.schedule[dayIndex][slotIndex] = {
        ...updatedSchedule.schedule[dayIndex][slotIndex],
        [field]: value
      };
      return updatedSchedule;
    });

    // Clear any error messages that might be related to time slots
    if (error && error.includes('time slot')) {
      setError(null);
    }
  };

  const validateSchedule = () => {
    // If default schedule, from_date and to_date must be null
    if (scheduleForm.is_default_schedule) {
      if (scheduleForm.from_date || scheduleForm.to_date) {
        // We'll handle this automatically in submission, no need for an error
        // Just log for debugging
        console.log('Default schedule should have null date range. This will be fixed automatically.');
      }
    }
    // Check date validity when not a default schedule
    else {
      if (!scheduleForm.from_date || !scheduleForm.to_date) {
        setError('From and To dates are required when not a default schedule');
        return false;
      }

      if (dayjs(scheduleForm.from_date).isAfter(dayjs(scheduleForm.to_date))) {
        setError('To date must be after From date');
        return false;
      }
    }

    // Check all time slots have valid from and to times, and check for overlaps
    for (const dayIndex of Object.keys(scheduleForm.schedule)) {
      const daySlots = scheduleForm.schedule[dayIndex];
      const dayName = weekDays.find(d => d.value === dayIndex)?.label || dayIndex;

      // First validate each slot has valid from/to times
      for (let i = 0; i < daySlots.length; i++) {
        const slot = daySlots[i];
        if (!slot.from || !slot.to) {
          setError(`Missing time values in ${dayName}`);
          return false;
        }
        if (slot.from >= slot.to) {
          setError(`End time must be after start time in ${dayName}`);
          return false;
        }
      }

      // Then check for overlaps between slots
      if (daySlots.length > 1) {
        // Sort slots by start time to make overlap detection easier
        const sortedSlots = [...daySlots].sort((a, b) => a.from.localeCompare(b.from));

        for (let i = 0; i < sortedSlots.length - 1; i++) {
          const currentSlot = sortedSlots[i];
          const nextSlot = sortedSlots[i + 1];

          if (currentSlot.to > nextSlot.from) {
            setError(`Overlapping time slots detected in ${dayName}. The slot ${currentSlot.from}-${currentSlot.to} overlaps with ${nextSlot.from}-${nextSlot.to}`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSchedule()) return;

    try {
      setIsSaving(true);
      setError(null);

      // Ensure that from_date and to_date are null when is_default is true
      let submissionForm = { ...scheduleForm };
      if (submissionForm.is_default_schedule) {
        submissionForm.from_date = null;
        submissionForm.to_date = null;
      }

      // Sort all time slots for each day in ascending order
      const sortedScheduleForm = {
        ...submissionForm,
        schedule: Object.keys(submissionForm.schedule).reduce((sorted, dayIndex) => {
          sorted[dayIndex] = [...submissionForm.schedule[dayIndex]]
            .sort((a, b) => a.from.localeCompare(b.from));
          return sorted;
        }, {})
      };

      // Check if trying to set as default when another default already exists
      if (sortedScheduleForm.is_default_schedule) {
        const existingDefault = schedules.find(
          s => s.is_default_schedule && s.id !== sortedScheduleForm.id
        );

        if (existingDefault) {
          if (!window.confirm(
            'Another default schedule already exists for this doctor. Would you like to replace it with this schedule as the default?'
          )) {
            setIsSaving(false);
            return;
          }

          // Deactivate the existing default schedule
          await clinicService.updateDoctorSchedule(
            clinicId,
            selectedDoctor.id,
            { ...existingDefault, is_default_schedule: false }
          );
        }
      }

      // Use different API calls for create vs. update
      if (editingScheduleId) {
        // Update existing schedule
        await clinicService.updateDoctorSchedule(
          clinicId,
          selectedDoctor.id,
          sortedScheduleForm
        );
        setSuccessMessage('Schedule updated successfully');
      } else {
        // Create new schedule
        await clinicService.createDoctorSchedule(
          clinicId,
          selectedDoctor.id,
          sortedScheduleForm
        );
        setSuccessMessage('Schedule created successfully');
      }

      invalidateScheduleExpiryCache();

      // Refresh schedules list
      const data = await clinicService.getDoctorSchedules(clinicId, selectedDoctor.id);
      setSchedules(formatSchedules(data.schedules));

      // Close form
      setFormVisible(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError('Failed to save schedule. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormVisible(false);
    setEditingScheduleId(null);
    setError(null);
  };

  const handleCopyDaySchedule = (fromDay, toDay) => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };
      updatedSchedule.schedule[toDay] = [...(updatedSchedule.schedule[fromDay] || [])].map(slot => ({...slot}));
      return updatedSchedule;
    });
  };

  const handleApplyToAllDays = (fromDay) => {
    if (!window.confirm('Are you sure you want to apply this day\'s schedule to all other days?')) {
      return;
    }

    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };
      const sourceDaySlots = [...(updatedSchedule.schedule[fromDay] || [])].map(slot => ({...slot}));

      // Apply to all days
      for (const day of Object.keys(updatedSchedule.schedule)) {
        if (day !== fromDay) {
          updatedSchedule.schedule[day] = [...sourceDaySlots];
        }
      }

      return updatedSchedule;
    });
  };

  const handleDeleteSchedule = async (schedule) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      setLoading(true);

      // Call the API to delete the schedule
      await clinicService.deleteDoctorSchedule(clinicId, selectedDoctor.id, schedule.id);
      invalidateScheduleExpiryCache();
      setSuccessMessage('Schedule deleted successfully');

      // Refresh schedules list
      const refreshedData = await clinicService.getDoctorSchedules(clinicId, selectedDoctor.id);
      setSchedules(formatSchedules(refreshedData.schedules));

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Failed to delete schedule. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate duration between two time strings (HH:MM)
  const calculateDuration = (startTime, endTime) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    let durationMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);

    // Handle negative duration (shouldn't happen with validation)
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60; // Add 24 hours in minutes
    }

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    // Format as "Xh Ym" or just "Xh" or just "Ym"
    let duration = '';
    if (hours > 0) {
      duration += `${hours}h `;
    }
    if (minutes > 0 || hours === 0) {
      duration += `${minutes}m`;
    }

    return duration.trim();
  };

  const handleSortDaySlots = (dayIndex) => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };

      // Sort slots by start time
      updatedSchedule.schedule[dayIndex] = [...updatedSchedule.schedule[dayIndex]]
        .sort((a, b) => a.from.localeCompare(b.from));

      return updatedSchedule;
    });
  };

  // Add Sort All Days button in the form
  const handleSortAllDays = () => {
    setScheduleForm(prev => {
      const updatedSchedule = { ...prev };

      // Sort all days
      for (const dayIndex of Object.keys(updatedSchedule.schedule)) {
        if (updatedSchedule.schedule[dayIndex]?.length > 1) {
          updatedSchedule.schedule[dayIndex] = [...updatedSchedule.schedule[dayIndex]]
            .sort((a, b) => a.from.localeCompare(b.from));
        }
      }

      return updatedSchedule;
    });
  };

  if (loading && doctors.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <p className="text-gray-500">Loading doctors and schedules...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Module Requirement</p>
            <p className="text-sm">
              This feature requires the <span className="font-bold">Structured Data</span> module to be enabled.
              Doctor schedules managed here will only be used when this module is active.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Doctor Schedules</h3>
        {selectedDoctor && (
          <button
            onClick={handleAddSchedule}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Schedule
          </button>
        )}
      </div>

      <DoctorSchedulesJsonPanel
        clinicId={clinicId}
        onImported={async () => {
          if (!selectedDoctor) return;
          try {
            setLoading(true);
            const data = await clinicService.getDoctorSchedules(clinicId, selectedDoctor.id);
            setSchedules(formatSchedules(data.schedules));
            setSuccessMessage('Schedules refreshed after JSON import');
            setTimeout(() => setSuccessMessage(''), 3000);
          } catch (err) {
            console.error('Error refreshing schedules after JSON import:', err);
            setError('Imported, but failed to refresh the schedule list.');
          } finally {
            setLoading(false);
          }
        }}
      />

      {/* Doctor selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Doctor:
        </label>
        <select
          value={selectedDoctor?.id || ''}
          onChange={handleDoctorSelect}
          className="w-full md:w-1/2 p-2 border rounded"
          disabled={doctors.length === 0}
        >
          {doctors.length === 0 && (
            <option value="">No doctors available</option>
          )}
          {doctors.map(doctor => (
            <option key={doctor.id} value={doctor.id}>
              {doctor.primary_name} - {doctor.speciality}
            </option>
          ))}
        </select>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Schedule Form */}
      {formVisible && (
        <div className="mb-8 p-6 border rounded-lg bg-gray-50">
          <h4 className="text-lg font-medium mb-4">
            {editingScheduleId ? 'Edit Schedule' : 'Add New Schedule'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="from_date"
                    value={scheduleForm.from_date || ''}
                    onChange={handleFormInputChange}
                    className={`w-full p-2 border rounded ${
                      scheduleForm.is_default_schedule ? 'bg-gray-100 text-gray-400' : ''
                    }`}
                    required={!scheduleForm.is_default_schedule}
                    disabled={scheduleForm.is_default_schedule}
                  />
                  {scheduleForm.is_default_schedule && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded pointer-events-none">
                      <span className="text-xs text-gray-500">Not applicable for default schedule</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="to_date"
                    value={scheduleForm.to_date || ''}
                    onChange={handleFormInputChange}
                    className={`w-full p-2 border rounded ${
                      scheduleForm.is_default_schedule ? 'bg-gray-100 text-gray-400' : ''
                    }`}
                    required={!scheduleForm.is_default_schedule}
                    disabled={scheduleForm.is_default_schedule}
                  />
                  {scheduleForm.is_default_schedule && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded pointer-events-none">
                      <span className="text-xs text-gray-500">Not applicable for default schedule</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center h-5">
                <input
                  id="is_default_schedule"
                  name="is_default_schedule"
                  type="checkbox"
                  checked={scheduleForm.is_default_schedule}
                  onChange={handleDefaultScheduleChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="is_default_schedule" className="font-medium text-gray-700">
                  Default Schedule
                </label>
                <p className="text-gray-500">
                  When enabled, this schedule will be used as the default for this doctor.
                  <span className="text-amber-600 ml-1">Only one default schedule can exist per doctor.</span>
                  <br />
                  <span className="text-gray-500 italic">Default schedules do not have date restrictions (from/to dates will be ignored).</span>
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center h-5">
                <input
                  id="active"
                  name="active"
                  type="checkbox"
                  checked={scheduleForm.active}
                  onChange={handleFormInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="active" className="font-medium text-gray-700">
                  Active
                </label>
                <p className="text-gray-500">
                  When enabled, this schedule will be active for the doctor.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <h5 className="text-md font-medium mb-4">Weekly Schedule</h5>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-gray-500">Configure time slots for each day of the week</span>
                <button
                  type="button"
                  onClick={handleSortAllDays}
                  className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                  title="Sort all time slots by start time"
                >
                  Sort All Days
                </button>
              </div>
              <div className="space-y-6">
                {weekDays.map((day) => (
                  <div key={day.value} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 p-3 font-medium border-b flex justify-between items-center">
                      <span>{day.label}</span>
                      <div className="flex gap-2">
                        <div className="relative inline-block text-left">
                          <select
                            onChange={(e) => handleCopyDaySchedule(e.target.value, day.value)}
                            className="bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            defaultValue=""
                          >
                            <option value="" disabled>Copy from...</option>
                            {weekDays.filter(copyDay => copyDay.value !== day.value).map(copyDay => (
                              <option key={copyDay.value} value={copyDay.value}>
                                {copyDay.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleApplyToAllDays(day.value)}
                          className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                          title="Apply this day's schedule to all days"
                        >
                          Apply to All
                        </button>
                        {scheduleForm.schedule[day.value]?.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleSortDaySlots(day.value)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                            title="Sort time slots by start time"
                          >
                            Sort Slots
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAddTimeSlot(day.value)}
                          className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                        >
                          Add Slot
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      {scheduleForm.schedule[day.value]?.length === 0 ? (
                        <p className="text-gray-500 text-sm py-2">No time slots defined for {day.label}</p>
                      ) : (
                        <div className="space-y-3">
                          {overlappingSlots[day.value] && (
                            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs mb-3">
                              <div className="flex items-start">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span>
                                  <strong>Warning:</strong> Overlapping time slots detected. Please adjust the times to ensure slots don't overlap.
                                </span>
                              </div>
                            </div>
                          )}
                          {scheduleForm.schedule[day.value]?.map((slot, slotIndex) => (
                            <div key={slotIndex} className="flex items-center gap-3">
                              <div className="w-1/3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Start Time
                                </label>
                                <input
                                  type="time"
                                  value={slot.from}
                                  onChange={(e) => handleTimeSlotChange(day.value, slotIndex, 'from', e.target.value)}
                                  className={`w-full p-2 border rounded ${
                                    overlappingSlots[day.value]?.includes(slotIndex) ? 'border-red-500 bg-red-50' : ''
                                  }`}
                                />
                              </div>
                              <div className="w-1/3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  End Time
                                </label>
                                <input
                                  type="time"
                                  value={slot.to}
                                  onChange={(e) => handleTimeSlotChange(day.value, slotIndex, 'to', e.target.value)}
                                  className={`w-full p-2 border rounded ${
                                    overlappingSlots[day.value]?.includes(slotIndex) ? 'border-red-500 bg-red-50' : ''
                                  }`}
                                />
                              </div>
                              <div className="flex items-end h-full">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTimeSlot(day.value, slotIndex)}
                                  className="p-2 text-red-500 hover:text-red-700"
                                  title="Remove time slot"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : (editingScheduleId ? 'Update Schedule' : 'Create Schedule')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Schedules List */}
      {!formVisible && (
        <div className="mt-6">
          <h4 className="text-md font-medium mb-4">Existing Schedules</h4>
          {!selectedDoctor ? (
            <p className="text-gray-500">Please select a doctor to view schedules.</p>
          ) : loading ? (
            <p className="text-gray-500">Loading schedules...</p>
          ) : schedules.length === 0 ? (
            <p className="text-gray-500">No schedules found for this doctor.</p>
          ) : (
            <div className="space-y-4">
              {schedules.map(schedule => (
                <div key={schedule.id} className="mt-3 border rounded bg-white p-3">
                  <div className="mb-3 flex justify-between items-center">
                    <h6 className="font-medium text-sm">
                      <span className="mr-2">Schedule {schedule.id.substring(0, 8)}</span>
                      {schedule.is_default_schedule && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Default</span>
                      )}
                      {!schedule.active && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">Inactive</span>
                      )}
                    </h6>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditSchedule(schedule)}
                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(schedule)}
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {schedule.is_default_schedule ?
                      'Default schedule (no date restrictions)' :
                      `Valid from ${dayjs(schedule.from_date).format('MMM D, YYYY')} to ${dayjs(schedule.to_date).format('MMM D, YYYY')}`
                    }
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          {weekDays.map(day => (
                            <th key={day.value} className="px-2 py-1 text-left border-b font-medium text-gray-700">{day.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {weekDays.map(day => (
                            <td key={day.value} className="p-2 align-top border-r last:border-r-0">
                              {schedule.schedule[day.value]?.length > 0 ? (
                                <ul className="space-y-1">
                                  {schedule.schedule[day.value].map((slot, idx) => (
                                    <li key={idx} className="text-xs whitespace-nowrap">
                                      <span className="text-gray-700">{slot.from} - {slot.to}</span>
                                      <span className="ml-1 text-gray-500">({calculateDuration(slot.from, slot.to)})</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-gray-500">No slots</p>
                              )}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorSchedulesSection;
